import {Location} from "@angular/common";
import {
  Component,
  computed,
  DOCUMENT,
  effect,
  inject,
  input,
  LOCALE_ID,
  numberAttribute,
  signal,
  ViewEncapsulation,
} from "@angular/core";
import type {MetaDefinition} from "@angular/platform-browser";
import {Meta} from "@angular/platform-browser";
import {Router} from "@angular/router";
import type {Observable} from "rxjs";
import {map, shareReplay, zip} from "rxjs";
import {optionallyTransformed} from "../../helpers/InputTransforms";
import type {SafeValueResourceRef} from "../../helpers/ResourceHelpers";
import {rxSafeResource} from "../../helpers/ResourceHelpers";
import {Header} from "../../layout/header/header";
import type {IPaginated} from "../../models/IPaginated";
import type {News} from "../../models/News/News";
import {NewsEntry} from "../../models/News/NewsEntry";
import {isEqualNewsFilter, type NewsFilter} from "../../models/NewsFilter";
import {NewsApi} from "../../services/api/news-api";
import {NewsShear} from "./news-shear/news-shear";

@Component({
  selector: "app-news-list-or-details",
  templateUrl: "./news-list-or-details.html",
  styleUrl: "./news-list-or-details.scss",
  // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
  encapsulation: ViewEncapsulation.None,
  imports: [
    Header,
    NewsShear,
  ],
})
export default class NewsListOrDetails {
  private readonly router = inject(Router);
  private readonly newsApi = inject(NewsApi);
  private readonly location = inject(Location);
  protected readonly rssUrl = this.router.serializeUrl(this.router.createUrlTree([`/${inject(LOCALE_ID)}`, "news", "rss"]));

  public readonly id = input<number | undefined, string | number | undefined>(undefined, {transform: optionallyTransformed(numberAttribute)});

  private readonly currentOffset = signal(0);
  protected readonly filter = signal<NewsFilter | undefined>(((): NewsFilter | undefined => {
    const state = this.location.getState();
    if (typeof state !== "object" || state === null) return undefined;
    const typedState: { filter?: NewsFilter } = state;
    return typedState.filter;
  })(), {equal: (lhs, rhs) => lhs === undefined || rhs === undefined ? lhs === rhs : isEqualNewsFilter(lhs, rhs)});

  private lastNews?: Observable<IPaginated<NewsEntry>>;
  private readonly entryOrList: SafeValueResourceRef<NewsEntry | IPaginated<NewsEntry> | undefined> = rxSafeResource({
    params: () => ({
      id: this.id(),
      offset: this.currentOffset(),
      filter: this.filter(),
    }),
    stream: ({params: {id, offset, filter}}): Observable<NewsEntry | IPaginated<NewsEntry>> => {
      if (id !== undefined) return this.newsApi.getNewsById(id);
      let loaded: Observable<News>;
      switch (filter?.key) {
        case undefined:
          loaded = this.newsApi.getNews({offset});
          break;
        case "author":
          loaded = this.newsApi.getNewsByAuthor(filter.value.raw, {offset});
          break;
        case "tag":
          loaded = this.newsApi.getNewsByTag(filter.value.raw, {offset});
          break;
      }
      loaded = loaded.pipe(shareReplay(1));
      this.lastNews = offset === 0 || !this.lastNews ? loaded : zip([this.lastNews, loaded]).pipe(
        map(([existing, newLoaded]) => ({
          entries: existing.entries.concat(newLoaded.entries),
          total: newLoaded.total,
        })),
      );
      return this.lastNews;
    },
  });

  protected readonly isDetail = computed(() => this.id() !== undefined);

  protected readonly newsList = computed(() => {
    const entryOrList = this.entryOrList.valueOrDefault();
    return entryOrList instanceof NewsEntry ? undefined : entryOrList;
  });
  protected readonly newsEntry = computed(() => {
    const entryOrList = this.entryOrList.valueOrDefault();
    return entryOrList instanceof NewsEntry ? entryOrList : undefined;
  });


  public constructor() {
    const meta = inject(Meta);
    const document = inject(DOCUMENT);
    effect(() => this.updateMetaTags(meta, this.newsEntry()));
    effect(() => this.updateRssLink(document, this.isDetail()));
  }

  private updateRssLink(document: Document, isDetail: boolean): void {
    const rssLinkId = "head-rss-link";
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    let rssLink = document.getElementById(rssLinkId) as HTMLLinkElement | undefined;
    if (isDetail) {
      rssLink?.remove();
      return;
    }
    if (!rssLink) {
      rssLink = document.createElement("link");
      rssLink.id = rssLinkId;
      rssLink.rel = "alternate";
      rssLink.type = "application/rss+xml";
      rssLink.href = this.rssUrl;
      document.head.appendChild(rssLink);
    }
  }

  private updateMetaTags(meta: Meta, news?: Readonly<NewsEntry>): void {
    if (news?.author.fediverseHandle !== undefined) {
      const metaDef: MetaDefinition = {
        name: "fediverse:creator",
        content: news.author.fediverseHandle,
      };
      if (meta.addTag(metaDef)?.content !== metaDef.content) {
        // In case it already existed, we need to update it
        meta.updateTag(metaDef);
      }
    } else {
      meta.removeTag("name=\"fediverse:creator\"");
    }
  }

  protected async setFilter(filter: NewsFilter, isDetail: boolean): Promise<void> {
    if (isDetail) {
      await this.router.navigate(["/news"], {state: {filter}});
    } else {
      this.currentOffset.set(0);
      this.filter.set(filter);
    }
  }

  protected resetFilter(): void {
    this.currentOffset.set(0);
    this.filter.set(undefined);
  }

  protected loadMore(currentLength: number): void {
    this.currentOffset.set(currentLength);
  }
}
